import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, XIcon } from 'lucide-react';
import { recentPurchases } from '../data/recentPurchases';
import { offer } from '../data/offer';
const FIRST_DELAY = 6000;
const VISIBLE_MS = 5500;
const GAP_MS = 12000;

/**
 * Low-frequency "someone just bought this" notification, bottom-left so it
 * never covers the CTA. Stays hidden until real orders are added to
 * data/recentPurchases.ts.
 */
export function PurchaseNotifications() {
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const hasData = recentPurchases.length > 0;
  useEffect(() => {
    if (dismissed || !hasData) return;
    let timeout: ReturnType<typeof setTimeout>;
    const show = () => {
      setVisible(true);
      timeout = setTimeout(hide, VISIBLE_MS);
    };
    const hide = () => {
      setVisible(false);
      timeout = setTimeout(() => {
        setIndex(i => (i + 1) % recentPurchases.length);
        show();
      }, GAP_MS);
    };
    timeout = setTimeout(show, FIRST_DELAY);
    return () => clearTimeout(timeout);
  }, [dismissed, hasData]);
  if (dismissed || !hasData) return null;
  const purchase = recentPurchases[index];
  const minutes = 2 + index * 7 % 26;
  return <div aria-live="polite" className="pointer-events-none fixed bottom-4 left-4 z-40 max-w-[calc(100vw-2rem)] sm:bottom-6 sm:left-6" data-id="element-268" data-mp="1cjfojdku8ar2">
      <AnimatePresence data-id="element-269" data-mp="17i015ev6ts5i">
        {visible && <motion.div key={index} initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 14,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 10
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto flex w-[320px] max-w-full items-start gap-3 rounded-xl border border-line bg-white p-3.5 shadow-card" data-id="element-270" data-mp="12k8aeo6a4c2w">
            <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full bg-green-deep" data-id="element-271" data-mp="3pqf9z8j4kc63">
              <CheckIcon className="h-4 w-4 text-white" aria-hidden="true" data-id="element-272" data-mp="3arla1lao9oxi" />
            </span>
            <div className="min-w-0 flex-1" data-id="element-273" data-mp="3fqwqet74msek">
              <p className="text-[13px] leading-snug text-ink" data-id="element-274" data-mp="3ftspwlymhp54">
                <span className="font-bold" data-id="element-275" data-mp="31zjb5rg94gpc">{purchase.name}</span> in {purchase.city},{' '}
                {purchase.state} just got {offer.productName}
              </p>
              <p className="mt-1 text-[11px] font-bold uppercase tracking-wide text-ink-muted" data-id="element-276" data-mp="2s2nc0cc8t6xg">
                {minutes} minutes ago
              </p>
            </div>
            <button type="button" onClick={() => setDismissed(true)} aria-label="Dismiss purchase notifications" className="-mr-1 -mt-1 flex h-7 w-7 flex-none items-center justify-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-light hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-court" data-id="element-277" data-mp="2wyvlpa7o87x8">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-278" data-mp="2dfewx0ftchg7" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}